import { Component, ElementRef, HostListener, OnInit, OnDestroy, ViewChild, inject, computed, ChangeDetectorRef } from '@angular/core';
import { filter, take } from 'rxjs';
import { AppConfig } from 'src/app/app.config';
import { ContentService } from 'src/app/service/content.service';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { ConfigStore } from 'src/app/store/config/config.store';
import { ConfigService } from 'src/services/config.service';
import { PrivacypolicyformService } from 'src/services/privacypolicyform.service';
import { Router, RouterLink } from '@angular/router';
import { CommonModule } from '@angular/common';
import { SHARED_IMPORTS } from 'src/app/shared/shared.module';
declare global {
  interface Window { onRecaptchaLoad?: () => void; grecaptcha: any; }
}
export interface RightsRequest {
  rights: string[];
  Relationship: string;
  Interaction?: string;
  FullName: string;
  TelephoneNumber?: string;
  EmailAddress: string;
  Country: string;
  FullAddress: string;
  City: string;
  ZipCode: string;
}

export interface RightOption {
  RightId: number;
  ShortDescription: string;
  Description: string;
  DetailText: string;
}

export interface CountryOption {
  CountryId: number;
  CountryName: string;
}

export interface EmailRequest {
  to: string;
  subject: string;
  body: RightsRequest;
  recaptchaToken : any;
}
declare const grecaptcha: any;
@Component({
  selector: 'app-privacy-policy-form',
  templateUrl: './privacy-policy-form.component.html',
  styleUrl: './privacy-policy-form.component.css',
  standalone: true,
  imports: [...SHARED_IMPORTS]
})
export class PrivacyPolicyFormComponent implements OnInit, OnDestroy {
  @ViewChild('rightsCombobox', { static: false }) rightsComboboxRef!: ElementRef;
  @ViewChild('captchaHost', { static: false }) captchaHost!: ElementRef<HTMLDivElement>;

  private readonly configStore = inject(ConfigStore);
  private readonly configService = inject(ConfigService); 
  requestForm: FormGroup;
  siteKey :any = "";
  recaptchaToken = '';
  captchaError = false;
  private widgetId: number | null = null;
  siteKeyConfig: string | null = null;
  privacyPolicyFormHtml = '';
  redirectUrl="";
  webRoot="";
  toEmail:any="";
  reCaptchaApiUrl:any;
  private viewReady = false; 
  constructor(private cdr: ChangeDetectorRef, private fb: FormBuilder,private router: Router,private contentService:ContentService, private privacyPolicyService: PrivacypolicyformService) {
    this.requestForm = this.fb.group({
      rights: [[], Validators.required],
      Relationship: [null, Validators.required],
      Interaction: [''],
      FullName: ['', Validators.required],
      TelephoneNumber: [''],
      EmailAddress: ['', [Validators.required, Validators.email]],
      Country: [null, Validators.required],
      FullAddress: ['', Validators.required],
      City: ['', Validators.required],
      ZipCode: ['', [Validators.required, Validators.pattern('^[0-9]{5}$')]]
    });
  }

  ngOnInit(): void { 
    this.webRoot = AppConfig.appsettings.navigatorRoot;
    this.redirectUrl = this.normalizeRedirectUrl(AppConfig.appsettings.redirectUrl);     
    this.contentService.getPrivacyPolicyFormHtml().subscribe({
      next: (html) => (this.privacyPolicyFormHtml = html),
      error: () => (this.privacyPolicyFormHtml = '<p>Unable to load privacy content.</p>')
    });

    this.configService.isConfigLoaded().subscribe(loaded => {
      if (loaded) {
         let configModel = this.configService.getConfigModel();
        this.siteKey = configModel.getConfigkeyname('RecaptchaSiteKey')??"";  
        this.toEmail = configModel.getConfigkeyname('PrivacyPolicyFormToEmail')??"";
        this.reCaptchaApiUrl = configModel.getConfigkeyname('RecaptchaApiUrl')??"";
        this.tryInitRecaptcha();        
      }
    }); 
    this.GetRightOptionList();
    this.GetCountryList(); 
    this.configService.isConfigLoaded()
      .pipe(filter((loaded) => loaded), take(1))
      .subscribe(() => this.GetRelationshipList());       
  }

  ngAfterViewInit(): void {
     this.viewReady = true;
    this.tryInitRecaptcha();
  }
  private tryInitRecaptcha(): void {
    if (!this.viewReady) return;
    if (!this.siteKey) return;

    this.initRecaptcha(); 
  }
  private initRecaptcha(): void {
    // already ready
    if (window.grecaptcha?.render || window.grecaptcha?.enterprise?.render) {
      this.renderCaptcha();
      return;
    }

    // global callback for explicit render
    window.onRecaptchaLoad = () => this.renderCaptcha();

    // avoid duplicate script tags
    if (!document.getElementById('recaptcha-api')) {
      const s = document.createElement('script');
      s.id = 'recaptcha-api';
      s.src = this.reCaptchaApiUrl;
      s.async = true;
      s.defer = true;
      document.body.appendChild(s);
    }
  }
  private renderCaptcha(): void {
    if (!this.captchaHost?.nativeElement || this.widgetId !== null) return;

    const api = window.grecaptcha?.render
      ? window.grecaptcha
      : window.grecaptcha?.enterprise; // fallback if enterprise script is injected

    if (!api?.render) {
      console.error('reCAPTCHA API not ready. Check consent/adblock/CSP.');
      return;
    }

    this.widgetId = api.render(this.captchaHost.nativeElement, {
      sitekey: this.siteKey,
      callback: (token: string) => { this.recaptchaToken = token; this.captchaError = false; },
      'expired-callback': () => { this.recaptchaToken = ''; }
    });
  }
  ngOnDestroy(): void {
    document.body.classList.remove('privacy-form-page');
  }

  private normalizeRedirectUrl(url: string): string {
    const source = (url && url.trim().length > 0) ? url.trim() : this.webRoot;
    if (!source) {
      return '/';
    }
    let normalized = source.replace(/\/admin\/?$/i, '/');
    if (!normalized.endsWith('/')) {
      normalized += '/';
    }
    return normalized;
  }

  formSubmitAttempted = false;
  isRightsDropdownOpen = false;
  rightsSearchTerm = '';
  activeRight: RightOption | null = null;

  rightsOptions: RightOption[] = [];

  relationshipOptions: { label: string; value: string }[] = [];

  countries: CountryOption[] = [];

  get selectedRights(): RightOption[] {
    const selected: number[] = this.requestForm.get('rights')?.value || [];
    return this.rightsOptions.filter(o => selected.includes(o.RightId));
  }

  get filteredRightsOptions(): RightOption[] {
    const term = this.rightsSearchTerm.trim().toLowerCase();
    if (!term) { return this.rightsOptions; }
    return this.rightsOptions.filter(o =>
      o.Description.toLowerCase().includes(term) ||
      o.ShortDescription.toLowerCase().includes(term)
    );
  } 

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (this.rightsComboboxRef && !this.rightsComboboxRef.nativeElement.contains(event.target)) {
      this.isRightsDropdownOpen = false;
    }
  }

  openDropdown(): void {
    this.isRightsDropdownOpen = true;
  }

  onRightsBlur(): void {
    this.requestForm.get('rights')?.markAsTouched();
  }

  shouldShowError(controlName: string, errorKey?: string): boolean {
    const control = this.requestForm.get(controlName);
    if (!control) {
      return false;
    }

    const touchedOrSubmitted = this.formSubmitAttempted || control.touched || control.dirty;
    if (!touchedOrSubmitted) {
      return false;
    }

    return errorKey ? control.hasError(errorKey) : control.invalid;
  }

  onZipCodeInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    const sanitized = (input.value || '').replace(/\D/g, '').slice(0, 5);
    input.value = sanitized;
    this.requestForm.get('ZipCode')?.setValue(sanitized);
  }

  onTelephoneInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    const sanitized = (input.value || '').replace(/\D/g, '');
    input.value = sanitized;
    this.requestForm.get('TelephoneNumber')?.setValue(sanitized);
  }

  toggleRight(option: RightOption): void {
    const current: number[] = [...(this.requestForm.get('rights')?.value || [])];
    const idx = current.indexOf(option.RightId);
    if (idx >= 0) {
      current.splice(idx, 1);
      if (this.activeRight?.RightId === option.RightId) {
        this.activeRight = this.selectedRights.find(r => r.RightId !== option.RightId) ?? null;
      }
    } else {
      current.push(option.RightId);
      this.activeRight = option;
    }
    this.requestForm.get('rights')!.setValue(current);
    this.rightsSearchTerm = '';
    this.isRightsDropdownOpen = false;
  }

  setActiveRight(option: RightOption): void {
    this.activeRight = option;
  }

  removeRight(option: RightOption, event: Event): void {
    event.stopPropagation();
    const current: number[] = (this.requestForm.get('rights')?.value || [])
      .filter((id: number) => id !== option.RightId);
    this.requestForm.get('rights')!.setValue(current);
    if (this.activeRight?.RightId === option.RightId) {
      this.activeRight = this.selectedRights[0] ?? null;
    }
  }

  isRightSelected(id: number): boolean {
    return (this.requestForm.get('rights')?.value || []).includes(id);
  }

  clearControl(controlName: 'Relationship' | 'Country'): void {
    this.requestForm.get(controlName)?.setValue(null);
  }

  GetRightOptionList(): void {
      this.privacyPolicyService.GetRightOptionList().subscribe(
        (result: RightOption[]) => {
          this.rightsOptions =  result;
        },
        (error) => {
          console.error('Error fetching right options:', error);
          this.rightsOptions = [];
        }
      );
  }

  GetCountryList(): void {
      this.privacyPolicyService.GetCountryList().subscribe(
        (result: CountryOption[]) => {
          this.countries =  result;
        },
        (error) => {
          console.error('Error fetching countries:', error);
          this.countries = [];
        }
      );
  }

  GetRelationshipList(): void {
    const config = this.configStore.config();
    if (!config || !config.config) {
      this.relationshipOptions = [];
      return;
    }

    const relationshipConfig = config.config.find(item => item.keyText === 'Relationship');
    const values = relationshipConfig?.valText
      ?.split('|')
      .map((item) => item.trim())
      .filter((item) => item.length > 0) ?? [];

    this.relationshipOptions = values.map((value) => ({ label: value, value }));
     this.cdr.detectChanges();
  }
  redirectLandingPage(event: Event): void {
    event.preventDefault();
    window.location.href = this.redirectUrl;
  }

  submit(): void {
    this.formSubmitAttempted = true;
    
    if (this.requestForm.invalid) {
      console.log('Form is invalid');
      return;
    }
     if (!this.recaptchaToken) {
       this.captchaError = true;
       return;
     }
     const token = grecaptcha?.getResponse?.() || '';

     if (!token) {
       this.captchaError = true;
       return;
     }

     this.captchaError = false;

    const model: RightsRequest = {
      rights: this.selectedRights.map(r => r.RightId.toString()),
      Relationship: this.requestForm.get('Relationship')?.value,
      Interaction: this.requestForm.get('Interaction')?.value,
      FullName: this.requestForm.get('FullName')?.value,
      TelephoneNumber: this.requestForm.get('TelephoneNumber')?.value,
      EmailAddress: this.requestForm.get('EmailAddress')?.value,
      Country: this.requestForm.get('Country')?.value,
      FullAddress: this.requestForm.get('FullAddress')?.value,
      City: this.requestForm.get('City')?.value,
      ZipCode: this.requestForm.get('ZipCode')?.value
    }

    const emailRequest: EmailRequest = {
      to: this.toEmail, 
      subject: 'Customer Privacy Request Details',
      body: model,
      recaptchaToken: token
    };

    this.privacyPolicyService.Submit(emailRequest).subscribe((result: any) => {
        if(result && result.success) {
         this.requestForm.reset({
           rights: [],
           Relationship: null,
           Interaction: '',
           FullName: '',
           TelephoneNumber: '',
           EmailAddress: '',
           Country: null,
           FullAddress: '',
           City: '',
           ZipCode: ''
         });

         this.formSubmitAttempted = false;
         this.requestForm.markAsPristine();
         this.requestForm.markAsUntouched();
         this.requestForm.updateValueAndValidity();

         // optional UI resets
         this.rightsSearchTerm = '';
         this.activeRight = null;
         this.captchaError = false;
          console.log('Email sent successfully:', result);
        }
        else {
          console.error('Error sending email:', result);
        }
        grecaptcha.reset();
    });
  }

  handleLinkClick(event: MouseEvent): void {
    const target = event.target as HTMLElement;
    const anchor = target.closest('a') as HTMLAnchorElement | null;
    if (!anchor) return;

    const href = (anchor.getAttribute('href') || '').trim();
    if (!href) return;

    // Allow non-router links
    const isExternalProtocol =
      /^(mailto:|tel:|https?:|ftp:)/i.test(href);

    if (isExternalProtocol) {
      // Let browser handle mailto/tel/http
      return;
    }

    // Handle internal Angular routes only
    event.preventDefault();
    this.router.navigateByUrl(href);
  }
}
